// src/lib/utils/csv.ts

/**
 * Minimal, dependency-free CSV helpers.
 *
 * `rowsToCsv` is pure (and unit-tested) so the serialisation logic can be
 * verified without a DOM; `downloadCsv` wraps it in a browser blob download
 * for client components (e.g. the Marketing Budget Calculator's export).
 */

/** Leading characters a spreadsheet may interpret as the start of a formula. */
const FORMULA_TRIGGER = /^[=+\-@\t\r]/

/**
 * Cell escaping: (1) neutralize CSV/formula injection — a string cell that a
 * spreadsheet would treat as a formula (leading `=`, `+`, `-`, `@`, tab, or CR)
 * is prefixed with a single quote so Excel/Sheets render it as literal text;
 * (2) RFC-4180 quoting — wrap in double-quotes (doubling internal quotes) when
 * the value contains `,`, `"`, or a newline.
 *
 * The injection guard is restricted to `string` inputs, so numeric and
 * negative-number cells passed as numbers are never altered. (Authored template
 * CSVs with intentional `=SUM(...)` formulas are static files written directly,
 * not routed through this helper, so they are unaffected.)
 */
function escapeCell(value: string | number): string {
  let s = String(value ?? '')
  if (typeof value === 'string' && FORMULA_TRIGGER.test(s)) {
    s = `'${s}`
  }
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

/**
 * Serialise a 2D array of cells to a CSV string (CRLF line endings, the
 * RFC-4180 default that Excel and Google Sheets both import cleanly).
 */
export function rowsToCsv(rows: ReadonlyArray<ReadonlyArray<string | number>>): string {
  return rows.map((row) => row.map(escapeCell).join(',')).join('\r\n')
}

/**
 * Trigger a client-side download of the given rows as a `.csv` file.
 * No-op on the server (guards on `document`).
 */
export function downloadCsv(filename: string, rows: ReadonlyArray<ReadonlyArray<string | number>>): void {
  if (typeof document === 'undefined') return

  // Prepend a UTF-8 BOM so Excel reads non-ASCII correctly.
  const blob = new Blob(['﻿', rowsToCsv(rows)], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
